/**
 * Modelo do CCO a partir do tempo real: cidades → setores → equipamentos, com nota de anomalia e indicadores —
 * Nexora Grid (frontend).
 *
 * Criado em: 18/10/2026 · Última modificação: 25/10/2026 · Versão: 2.3.0
 *
 * v2.0 (GUS-1508/1504, tela nova do CCO = protótipo aprovado v4): a hierarquia do protótipo sobre os dados reais.
 * - **cidade** = município do tópico (nome e UF do catálogo; `exibir_cco = false` fica fora);
 * - **setor** = unidade do hub (código em maiúsculas, ex.: `JRD-RSE-09`, `RAP-001`);
 * - **equipamento** = dispositivo, com o tipo pelo cadastro (ou pelas medidas): reservatório, bomba, VRP, macromedidor.
 * Indicadores como no protótipo: abastecimento (macromedidores), produzida (bombas), reservação (média e lista),
 * bombeamento, pressão mínima por setor (VRP a jusante e rede), alarmes ativos (anomalias) e no dia, energia e kWh/m³.
 * A Clássica, a 3D e a Apresentação usam este mesmo modelo.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import type { Ativo } from "../cadastro/tipos";
import { anomalo, notaEquipamento, piorNota, type Nota, type Problema } from "./anomalia";
import { correnteNominal, tensaoNominal, useCco } from "./contexto";
import { nomeMedida } from "./medidas";
import { chave } from "./useTempoReal";
import { avaliacao } from "./Valor";

export type TipoEquip = "reservatorio" | "bomba" | "vrp" | "macro" | "poco" | "pressao" | "outro";
/** US-2004: saúde de uma integração por API (`/api/v1/conectores/saude`). */
export type SaudeConector = { nome: string; municipio: string | null; estado: "ok" | "parcial" | "atrasado" | "falhando" | "sem_ciclo" | "pausado";
  intervalo_segundos: number; equipamentos: number | null; sem_dado: { device_id: string; ultimo: string | null }[];
  ciclo: { em: string; ok_em: string | null; coletados: number; inseridos: number; descartados: number; falhas_seguidas: number; ultimo_erro: string | null } | null;
  alarme: { alarme_id: string; timestamp: string; descricao: string } | null };
export type Municipio = { codigo: string; nome: string; uf: string; ibge: string; simulado: boolean; cadastrado: boolean; exibir_cco?: boolean;
  fontes?: SaudeConector[] };
export type Equipamento = {
  device: string; nome: string; tipo: TipoEquip; setor: string; cidade: string; medidas: Set<string>; nota: Nota;
  ativos: Ativo[]; velho: boolean;
};
export type Indicadores = {
  abastecimento: number; produzida: number; energia: number; kwhm3: number;
  reservacao: number | null; reservatorios: { e: Equipamento; nivel: number }[];
  ligadas: number; bombas: number; pressaoMin: number | null; pressoes: { setor: string; p: number }[];
  ativos: Equipamento[]; alarmes24: number;
};
export type Setor = { codigo: string; titulo: string; cidade: string; equipamentos: Equipamento[]; nota: Nota; ind: Indicadores };
export type Cidade = { municipio: Municipio; setores: Setor[]; nota: Nota; ind: Indicadores };

export const TIPO_NOME: Record<TipoEquip, string> = { reservatorio: "Reservatório", bomba: "Bomba submersa", vrp: "VRP", macro: "Macromedidor",
  poco: "Poço", pressao: "Ponto de pressão", outro: "Equipamento" };
export const TIPO_ICONE: Record<TipoEquip, string> = { reservatorio: "▭", bomba: "⊙", vrp: "◇", macro: "◎", poco: "◒", pressao: "◌", outro: "▪" };
const TIPO_CADASTRO: Record<string, TipoEquip> = { reservatorio: "reservatorio", bomba: "bomba", poco_profundo: "bomba", elevatoria: "bomba",
  vrp: "vrp", macromedidor: "macro", ponto_pressao: "pressao" };
// P-027: tipo no cadastro global de dispositivos (quando o equipamento ainda não tem ativo na rede)
const TIPO_DISPOSITIVO: Record<string, TipoEquip> = { ...TIPO_CADASTRO, poco: "poco", ponto_pressao: "pressao" };

function tipoDe(medidas: Set<string>, ativos: Ativo[], tipoDispositivo?: string): TipoEquip {
  // US-2306: poço e ponto de pressão do cadastro global de dispositivos dizem o que a telemetria mede (Costa Rica)
  if (tipoDispositivo === "poco" || tipoDispositivo === "ponto_pressao") return TIPO_DISPOSITIVO[tipoDispositivo];
  const doCadastro = ativos.map((a) => TIPO_CADASTRO[a.dados.tipo]).find(Boolean);
  if (doCadastro) return doCadastro;
  if (tipoDispositivo && TIPO_DISPOSITIVO[tipoDispositivo]) return TIPO_DISPOSITIVO[tipoDispositivo];
  if (medidas.has("nivel_reservatorio_pct")) return "reservatorio";
  if (medidas.has("corrente_fase_a") || medidas.has("potencia_ativa_kw")) return "bomba";
  if (medidas.has("pressao_jusante_mca")) return "vrp";
  if (medidas.has("vazao_instantanea_m3h")) return "macro";
  return "outro";
}

/** Nome do equipamento: identificação do cadastro; sem cadastro, o código do dispositivo em maiúsculas. */
export function nomeDispositivo(device: string, ativos?: Ativo[]): string {
  const doCadastro = ativos?.map((a) => a.dados.identificacao).join(", ");
  if (doCadastro) return doCadastro;
  return device.toUpperCase();
}

/** Catálogo de municípios da operadora (código do tópico → nome, UF). Sem catálogo, o nome é o código.
 *  US-2004: cada município traz a saúde das integrações por API que o alimentam (`fontes`), relida a cada minuto. */
export function useMunicipios(empresa: string | null) {
  const [mapa, setMapa] = useState<Map<string, Municipio>>(new Map());
  useEffect(() => {
    if (!empresa) return;
    let vivo = true;
    const carregar = () => Promise.all([
      api<{ municipios: Municipio[] }>(`/v1/municipios?empresa=${empresa}`),
      api<SaudeConector[]>(`/v1/conectores/saude?empresa=${empresa}`).catch(() => [] as SaudeConector[]),
    ]).then(([r, saude]) => {
      if (!vivo) return;
      setMapa(new Map(r.municipios.map((m) => [m.codigo, { ...m, fontes: saude.filter((s) => s.municipio === m.codigo) }])));
    }).catch(() => undefined);
    void carregar();
    const relogio = window.setInterval(() => void carregar(), 60000);
    return () => { vivo = false; window.clearInterval(relogio); };
  }, [empresa]);
  return mapa;
}

export const fmt = (v: number | null | undefined, casas = 0) =>
  (v === null || v === undefined || Number.isNaN(v) ? "—" : v.toLocaleString("pt-BR", { maximumFractionDigits: casas, minimumFractionDigits: casas }));

export type Modelo = {
  cidades: Cidade[]; cliente: Indicadores;
  cidade: (codigo?: string) => Cidade | undefined; setor: (codigo?: string) => Setor | undefined; equipamento: (device?: string) => Equipamento | undefined;
  valor: (device: string, medida: string) => number | undefined;
};

/** O modelo inteiro do CCO, recalculado a cada leitura nova. */
export function useModelo(): Modelo {
  const { empresa, vivo, ativosPorDevice, alarmes } = useCco();
  const municipios = useMunicipios(empresa);
  return useMemo(() => {
    const valor = (device: string, medida: string) => vivo.leituras.get(chave(device, medida))?.valor;
    // dispositivos do fluxo, com unidade e município
    const disp = new Map<string, { device: string; unidade: string; municipio: string; medidas: Set<string> }>();
    vivo.leituras.forEach((l) => {
      const d = disp.get(l.device_id) ?? { device: l.device_id, unidade: l.unidade, municipio: l.municipio ?? "", medidas: new Set<string>() };
      d.medidas.add(l.medida);
      disp.set(l.device_id, d);
    });
    const naoReconhecidos = new Map<string, Problema[]>();
    alarmes.filter((g) => !g.reconhecido).forEach((g) => naoReconhecidos.set(g.device_id, [...(naoReconhecidos.get(g.device_id) ?? []),
      { origem: "alarme", prioridade: g.prioridade, motivo: g.descricao || g.tipo.replace(/_/g, " ").toLowerCase() }]));
    const noDia = new Map<string, number>();
    alarmes.forEach((g) => noDia.set(g.device_id, (noDia.get(g.device_id) ?? 0) + g.ocorrencias));

    const equipamentos: Equipamento[] = [];
    disp.forEach((d) => {
      const ativos = ativosPorDevice.get(d.device) ?? [];
      const problemas: Problema[] = [...(naoReconhecidos.get(d.device) ?? [])];
      d.medidas.forEach((m) => {
        const v = valor(d.device, m);
        const a = v === undefined ? null : avaliacao(d.device, m, v, correnteNominal(ativos), tensaoNominal(ativos, v));
        if (a) problemas.push({ origem: "medida", prioridade: a.prioridade, motivo: `${nomeMedida(m).toLowerCase()}: ${a.motivo}` });
      });
      const velho = vivo.velhos.has(d.device);
      equipamentos.push({ device: d.device, nome: nomeDispositivo(d.device, ativos), tipo: tipoDe(d.medidas, ativos, vivo.tipos.get(d.device)), setor: d.unidade,
        cidade: d.municipio, medidas: d.medidas, nota: notaEquipamento(problemas, velho), ativos, velho });
    });
    const porNota = <T extends { nota: Nota }>(a: T, b: T) => b.nota.nota - a.nota.nota;

    const indicadores = (eqs: Equipamento[], setoresDe: Setor[] | null): Indicadores => {
      const res = eqs.filter((e) => e.tipo === "reservatorio" && valor(e.device, "nivel_reservatorio_pct") !== undefined);
      const bombas = eqs.filter((e) => e.tipo === "bomba");
      const ligada = (b: Equipamento) => (valor(b.device, "bomba_ligada") ?? ((valor(b.device, "potencia_ativa_kw") ?? 0) > 0.5 ? 1 : 0)) >= 0.5;
      // P-027: em Costa Rica a vazão distribuída é medida no reservatório (pulsos), não em macromedidor
      const abastecimento = eqs.filter((e) => (e.tipo === "macro" || e.tipo === "reservatorio") && !e.velho)
        .reduce((t, e) => t + (valor(e.device, "vazao_instantanea_m3h") ?? 0), 0);
      const produzida = bombas.filter(ligada).reduce((t, b) => t + (valor(b.device, "vazao_instantanea_m3h") ?? 0), 0);
      const energia = bombas.reduce((t, b) => t + (valor(b.device, "potencia_ativa_kw") ?? 0), 0);
      const pressaoDe = (lista: Equipamento[]) => {
        const p = lista.flatMap((e) => [e.tipo === "vrp" ? valor(e.device, "pressao_jusante_mca") : undefined, valor(e.device, "pressao_rede_mca")])
          .filter((x): x is number => x !== undefined);
        return p.length ? Math.min(...p) : null;
      };
      const grupos = setoresDe ?? [...new Set(eqs.map((e) => e.setor))].map((s) => ({ codigo: s, equipamentos: eqs.filter((e) => e.setor === s) }) as Setor);
      const pressoes = grupos.map((s) => ({ setor: s.codigo.toUpperCase(), p: pressaoDe(s.equipamentos) }))
        .filter((x): x is { setor: string; p: number } => x.p !== null).sort((a, b) => a.p - b.p);
      const reservatorios = res.map((e) => ({ e, nivel: valor(e.device, "nivel_reservatorio_pct") ?? 0 }));
      return {
        abastecimento, produzida, energia, kwhm3: produzida > 0 ? energia / produzida : 0,
        reservacao: reservatorios.length ? reservatorios.reduce((t, r) => t + r.nivel, 0) / reservatorios.length : null, reservatorios,
        ligadas: bombas.filter(ligada).length, bombas: bombas.length, pressaoMin: pressoes[0]?.p ?? null, pressoes,
        ativos: eqs.filter((e) => anomalo(e.nota)).sort(porNota), alarmes24: eqs.reduce((t, e) => t + (noDia.get(e.device) ?? 0), 0),
      };
    };

    // município fora do CCO (exibir_cco = false): some das faixas e dos indicadores, mas o equipamento continua
    // acessível pelo endereço direto (links de Alarmes e da Rede, manobra dos CLPs de teste)
    const visivel = (codigo: string) => municipios.get(codigo)?.exibir_cco !== false;
    const todasCidades: Cidade[] = [...new Set(equipamentos.map((e) => e.cidade))].map((codigo) => {
      const daCidade = equipamentos.filter((e) => e.cidade === codigo);
      const setores: Setor[] = [...new Set(daCidade.map((e) => e.setor))].map((s) => {
        const eqs = daCidade.filter((e) => e.setor === s).sort((a, b) => porNota(a, b) || a.nome.localeCompare(b.nome));
        return { codigo: s, titulo: s.toUpperCase(), cidade: codigo, equipamentos: eqs, nota: piorNota(eqs.map((e) => e.nota)), ind: indicadores(eqs, null) };
      }).sort((a, b) => porNota(a, b) || a.codigo.localeCompare(b.codigo));
      const municipio = municipios.get(codigo) ?? { codigo, nome: codigo || "sem município", uf: "", ibge: "", simulado: false, cadastrado: false };
      return { municipio, setores, nota: piorNota(setores.map((s) => s.nota)), ind: indicadores(daCidade, setores) };
    }).sort((a, b) => Number(a.municipio.simulado) - Number(b.municipio.simulado) || a.municipio.nome.localeCompare(b.municipio.nome, "pt-BR"));
    // ordem estável das faixas (rede real primeiro, depois pelo nome): a anomalia vai para a fila, a faixa não pula
    const cidades = todasCidades.filter((c) => visivel(c.municipio.codigo));
    const todosSetores = todasCidades.flatMap((c) => c.setores);
    return {
      cidades, cliente: indicadores(equipamentos.filter((e) => visivel(e.cidade)), cidades.flatMap((c) => c.setores)),
      cidade: (codigo) => todasCidades.find((c) => c.municipio.codigo === codigo),
      setor: (codigo) => todosSetores.find((s) => s.codigo === codigo),
      equipamento: (device) => equipamentos.find((e) => e.device === device),
      valor,
    };
  // vivo.versao muda a cada lote de leituras
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vivo.versao, vivo.velhos, vivo.tipos, ativosPorDevice, alarmes, municipios]);
}
